import { FC, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { TableItem } from "../TableItem/TableItem";
import { PAGINATION_COUNT_STEP_SIX } from "constant";
import {
	getBatchObjects,
	getData,
	getSearchedBatchObjects,
	getSearchedData,
} from "toolkit/steps/stepSix/stepSix.thunks";
import { useParams } from "react-router-dom";
import styles from "./TableBody.module.sass";

export const TableBody: FC = () => {
	const dispatch = useAppDispatch();
	const scrollTargetRef = useRef<HTMLDivElement|null>(null);
	const [load, setLoad] = useState<boolean>(false);
	const { organizationId } = useParams();
	const {
		data,
		batchesMode,
		formationMode,
		batchObjects,
		currentBatch,
		searchMode,
		searchedData,
		searchValue,
		lastPart,
		lastSearchedPart,
	} = useAppSelector((state) => state.stepSix);

	useEffect(() => {
		if (scrollTargetRef.current) {
			scrollTargetRef.current.addEventListener("scroll", () => checkPosition());
		}

		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			if (scrollTargetRef.current) scrollTargetRef.current.removeEventListener("scroll", () => checkPosition());
		};
		//eslint-disable-next-line
	}, [scrollTargetRef.current, lastPart]);

	useEffect(() => {
		if (!searchMode && load && lastPart !== null) {
			if (batchesMode) {
				dispatch(
					getBatchObjects({
						organizationId: Number(organizationId),
						batchId: Number(currentBatch?.id),
						part: lastPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			} else {
				dispatch(
					getData({
						organizationId: Number(organizationId),
						formationMode,
						part: lastPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			}
		}

		if (searchMode && load && lastSearchedPart !== null) {
			if (batchesMode) {
				dispatch(
					getSearchedBatchObjects({
						organizationId: Number(organizationId),
						batchId: Number(currentBatch?.id),
						searchValue,
						part: lastSearchedPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			} else {
				dispatch(
					getSearchedData({
						organizationId: Number(organizationId),
						searchValue,
						formationMode,
						part: lastSearchedPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			}
		}
		//eslint-disable-next-line
	}, [load]);

	const checkPosition = () => {
		const height = scrollTargetRef.current?.scrollHeight;
		const screenHeight = scrollTargetRef.current?.offsetHeight;
		const scrolled = scrollTargetRef.current?.scrollTop;
		if (height && screenHeight && scrolled) {
			const threshold = height - screenHeight;
			const position = scrolled + screenHeight;

			if (position >= threshold && !load) setLoad(true);
		}
	};

	const localData = searchMode ? searchedData : batchObjects;

	return (
		<div ref={scrollTargetRef} className={styles["table-body-wrp"]}>
			<table className={styles["table-body"]}>
				<tbody className={styles.body}>
					{batchesMode &&
						localData.categorized.map((object, i) => <TableItem key={object.id} sequenceNumber={i} object={object} />)}
					{batchesMode &&
						localData.repeatable.map((object, i) => (
							<TableItem
								key={object.id}
								sequenceNumber={i + localData.categorized.length}
								object={object}
								repeatable={true}
							/>
						))}
					{batchesMode &&
						localData.uncategorized.map((object, i) => (
							<TableItem
								key={object.id}
								sequenceNumber={i + localData.categorized.length + localData.repeatable.length}
								object={object}
							/>
						))}

					{formationMode &&
						(searchMode ? searchedData : data).categorized.map((object, i) => (
							<TableItem key={object.id} sequenceNumber={i} object={object} />
						))}
					{formationMode &&
						(searchMode ? searchedData : data).repeatable
							.filter((object) => object.approvalDate && object.timeToSend)
							.map((object, i) => <TableItem key={object.id} sequenceNumber={i} object={object} repeatable={true} />)}

					{!batchesMode && !formationMode && (
						<>
							{(searchMode ? searchedData : data).categorized.map((object, i) => (
								<TableItem key={object.id} sequenceNumber={i} object={object} />
							))}
							{(searchMode ? searchedData : data).repeatable.map((object, i) => (
								<TableItem
									key={object.id}
									sequenceNumber={i + (searchMode ? searchedData : data).categorized.length}
									object={object}
									repeatable={true}
								/>
							))}
							{(searchMode ? searchedData : data).uncategorized.map((object, i) => (
								<TableItem
									key={object.id}
									sequenceNumber={
										i +
										(searchMode ? searchedData : data).categorized.length +
										(searchMode ? searchedData : data).repeatable.length
									}
									object={object}
								/>
							))}
						</>
					)}
				</tbody>
			</table>
		</div>
	);
};
